<template>
    <!-- Help for reports still on the table layout. Its wording describes
         that layout ("the table Report Templates", the Action menu's "Edit
         report"); the Group Reports table needs none. -->
    <slide-over-panel   v-if="! useTemplateCards"
                        v-model:show="showHelpPanel"
                        z-index="z-40"
                        mobile-bottom-sheet>
        <template   #title>
            Help
        </template>

        <template   #content>
            <div class="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
                <div class="flex items-center mb-2">
                    <tabler-icon name="users" size-class="h-4 w-4 text-blue-600 mr-2"></tabler-icon>
                    <strong class="text-blue-900">Group-Based Organization</strong>
                </div>
                <p class="text-sm text-gray-700">
                    Report templates are organized by groups. Templates inherit the group's zone access and can be sent to all group members.
                </p>
            </div>

            This is the configuration and running page for reports. On the table "Report Templates" sorted by
            group. Each report template describes a group of settings for the report, that will be sent to all the
            members of that group.

            There are actions that can be taken on a report template.

            <h3     class="text-left">Run for me</h3>
            Run the report w/ the template's settings for just you.

            <h3     class="text-left">Run for group</h3>
            Run the report w/ the template's settings for all members of the group.

            <h3     class="text-left">Edit report</h3>
            Edit the report template's settings.

            <h3     class="text-left">Archive template</h3>
            Archive the template to keep the template from activating in the future.
            All reports that were previously run with this template will continue to be available in the Report History page.

            <slot   name="report-help-text"></slot>
        </template>
    </slide-over-panel>

    <!-- Panel for Create Report -->
    <slide-over-panel   v-model:show="showCreateTemplatePanel"
                        z-index="z-40"
                        mobile-bottom-sheet>
        <template   #title>
            Create Report Template on {{ groupName }}
        </template>

        <template   #content>
            <!-- Padding only matters when the footer is pinned below. -->
            <div :class="useTemplateCards && canEdit ? 'pb-28' : ''">
                <!-- Ported reports get a plain labelled input; the others keep
                     the original centered two-column row. -->
                <div    v-if="useTemplateCards"
                        class="mb-4">
                    <label  for="create_template_name"
                            class="mb-2 block text-sm font-medium text-gray-700">
                        Template Name
                    </label>
                    <input  id="create_template_name"
                            v-model="$data._templateName"
                            type="text"
                            class="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                </div>

                <div    v-else
                        class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                    <span>Template Name</span>
                    <input      class="py-1 px-3 leading-normal rounded text-base border rounded"
                                type="text"
                                v-model="$data._templateName">
                </div>

                <slot   name="create-settings">
                </slot>
            </div>

            <!-- Ported reports pin Save to the bottom so it stays reachable
                 however long the settings list gets. -->
            <div    v-if="canEdit && useTemplateCards"
                    class="fixed bottom-0 right-0 z-10 w-screen max-w-2xl border-t border-gray-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6"
            >
                <button :disabled="isSaving"
                        class="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 hover:bg-blue-500 focus:border-blue-700 focus:outline-none active:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-600"
                        @click="createSettings"
                >
                    <tabler-icon   v-if="isSaving" name="loader" size-class="h-4 w-4 animate-spin mr-3"></tabler-icon>
                    {{ isSaving ? 'Saving Settings, Please Wait' : 'Save Settings' }}
                </button>
            </div>

            <!-- Create Template Button -->
            <!-- TODO. Fix Permission -->
            <div    v-if="canEdit && ! useTemplateCards"
                    class="flex justify-center">
                <button v-if="isSaving"
                        class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white rounded opacity-50 cursor-not-allowed">
                    <tabler-icon name="loader" size-class="h-4 w-4 animate-spin mr-3"></tabler-icon>
                    Saving Settings, Please Wait
                </button>
                <button v-else
                        class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white hover:bg-blue-600 hover:text-white
                            focus:outline-none focus:ring-blue-200 focus:border-blue-400 active:bg-blue-600"
                        @click="createSettings">
                    Save Settings
                </button>
            </div>
        </template>
    </slide-over-panel>

    <!-- Panel for Edit Report -->
    <slide-over-panel   v-model:show="showEditTemplatePanel"
                        z-index="z-40"
                        mobile-bottom-sheet>
        <template   #title>
            <!-- The default-settings row is not a group template, so it has no
                 group name to hang the title on -- "Edit Report Template on "
                 with a trailing blank read as a bug. Title it for what it is. -->
            <template   v-if="isSavingDefaultTemplate">
                Edit Default Report Settings
            </template>
            <template   v-else>
                Edit Report Template on {{ groupName }}
            </template>
        </template>

        <template   #content>
            <!-- Padding only matters when the footer is pinned below. -->
            <div :class="useTemplateCards && canEdit ? 'pb-28' : ''">
            <!-- Ported reports get a plain labelled input. The floating-label
                 version rendered its own "Template Name" on top of the one
                 beside it, showing the same words twice, but it is what the
                 unported reports still use. -->
            <!-- The default settings row has a fixed stored name that the
                 backend looks it up by, so the field was already readonly.
                 Showing a name field at all implied it was renameable, so it is
                 hidden there rather than shown greyed out. -->
            <div    v-if="useTemplateCards && ! isSavingDefaultTemplate"
                    class="mb-4">
                <label  for="edit_template_name"
                        class="mb-2 block text-base font-medium text-gray-700">
                    Template Name
                </label>
                <input  id="edit_template_name"
                        v-model="$data._templateName"
                        type="text"
                        class="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
            </div>

            <!-- Editing the shared row happens in the same panel as editing a
                 template, so the scope is restated here where the change is
                 actually made. -->
            <div    v-if="useTemplateCards && isSavingDefaultTemplate"
                    class="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <tabler-icon    name="alertTriangle"
                                class="mt-0.5 shrink-0 text-amber-600"
                ></tabler-icon>
                <p class="m-0 text-base text-amber-900">
                    You are editing the shared default settings for this report.
                    Saving changes them for everyone on this site.
                </p>
            </div>

            <!-- Unported reports keep the original centered floating-label row.
                 This must key off !useTemplateCards, NOT a bare v-else: the
                 default-settings amber banner above breaks the if/else chain, so
                 a plain v-else here also rendered for a ported group template and
                 showed "Template Name" twice. -->
            <div    v-if="! useTemplateCards"
                    class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                <span>Template Name</span>
                <floating-input     v-model:value="$data._templateName"
                                    :readonly="isSavingDefaultTemplate"
                                    class="text-left"
                                    label="Template Name"
                ></floating-input>
            </div>

            <slot   name="edit-settings">
            </slot>

            <!-- Who receives this on schedule. Only the shared default settings
                 row carries its own recipient list (BaseReport reads it when
                 isLegacyReporting); a group template sends to the group's
                 members instead, so it shows who that is rather than an input
                 that would not be honoured.

                 This list is stored on the shared row, so it is site-wide too --
                 the label says so rather than implying it is the editor's own. -->
            <div    v-if="useTemplateCards && showRecipientPicker"
                    class="mb-4">
                <user-select-input  v-model="$data._settings['subscribedUsers']"
                                    :users="users"
                                    label="Users to Send to Automatically (shared)"
                                    modal-title="Choose recipients"
                                    modal-description="They will be emailed this report each time it runs on schedule. This list is shared by everyone on this site."
                                    choose-label="Choose recipients"
                                    change-label="Change recipients"
                                    empty-summary="No users selected"
                                    empty-message="No users are available to receive this report."
                                    noun="user"
                                    plural-noun="users"
                ></user-select-input>
            </div>

            <div    v-else-if="useTemplateCards && $data._groupId !== 0"
                    class="mb-4">
                <label class="mb-2 block text-sm font-medium text-gray-700">
                    Users to Send to Automatically
                </label>

                <!-- A count and a link rather than every name: a large group
                     would otherwise fill the panel with chips, and membership
                     is not editable from here anyway. -->
                <div class="flex flex-col gap-2 rounded-md border border-gray-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
                    <div class="min-w-0">
                        <p  class="m-0 text-sm"
                            :class="groupMemberCount > 0 ? 'font-medium text-gray-900' : 'text-gray-500'">
                            {{ groupMemberSummary }}
                        </p>
                        <p class="m-0 mt-0.5 text-sm text-gray-500">
                            Everyone in {{ groupName ?? 'this group' }} receives this report.
                        </p>
                    </div>

                    <a  :href="groupSettingsUrl"
                        target="_blank"
                        rel="noopener"
                        class="inline-flex shrink-0 items-center gap-1.5 self-start rounded-md border border-blue-300 bg-white px-3 py-1.5 text-sm font-medium leading-5 text-blue-700 no-underline transition-colors duration-150 hover:border-transparent hover:bg-blue-100 hover:no-underline focus:outline-none focus:ring-2 focus:ring-blue-500 sm:self-auto"
                    >
                        <tabler-icon    name="users"
                                        size-class="h-3.5 w-3.5"
                        ></tabler-icon>
                        Manage group
                    </a>
                </div>
            </div>

            <!-- Original recipient control for unported reports. On the card
                 layout this is the UserSelectInput above, which shows the
                 group's own members when the template belongs to a group. -->
            <div    v-if="! useTemplateCards && isSavingDefaultTemplate && this.reportName !== 'nabp-report'"
                    class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                <span>Users to Send to Automatically</span>
                <select2
                    v-model:value="$data._settings['subscribedUsers']"
                    :multiple="true"
                    :options="users"
                ></select2>
            </div>
            </div>

            <!-- Ported reports pin Save to the bottom so it stays reachable
                 however long the settings list gets. -->
            <div    v-if="canEdit && useTemplateCards"
                    class="fixed bottom-0 right-0 z-10 w-screen max-w-2xl border-t border-gray-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6"
            >
                <button :disabled="isSaving"
                        class="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 hover:bg-blue-500 focus:border-blue-700 focus:outline-none active:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-600"
                        @click="saveSettings($data._currentReportTemplateIndex)"
                >
                    <tabler-icon   v-if="isSaving" name="loader" size-class="h-4 w-4 animate-spin mr-3"></tabler-icon>
                    {{ isSaving ? 'Saving Settings, Please Wait' : 'Save Settings' }}
                </button>
            </div>

            <!-- Save Template Button -->
            <!-- TODO. Fix Permission -->
            <div    v-if="canEdit && ! useTemplateCards"
                    class="flex justify-center pb-5">
                <button v-if="isSaving"
                        class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white rounded opacity-50 cursor-not-allowed">
                    <tabler-icon name="loader" size-class="h-4 w-4 animate-spin mr-3"></tabler-icon>
                    Saving Settings, Please Wait
                </button>
                <button v-else
                        class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white hover:bg-blue-600 hover:text-white
                            focus:outline-none focus:ring-blue-200 focus:border-blue-400 active:bg-blue-600"
                        @click="saveSettings($data._currentReportTemplateIndex)">
                    Save Settings
                </button>
            </div>

            <!-- List of Users in Group (Don't show for Legacy Report Template) -->
            <div    v-if="! useTemplateCards && $data._groupId !== 0 && numberOfUsersOnGroup($data._groupId) > 0"
                    class="pb-5">
                <div   class="text-center text-lg text-gray-600">Users on Group</div>

                <!-- List of users -->
                <div    class="flex flex-wrap">
                    <div    v-for="user in $data._usersByGroup[$data._groupId]"
                            class="flex items-start">
                        <span class="inline-flex items-center m-1 px-2.5 py-0.5 rounded-md text-md font-medium bg-blue-100 text-blue-800">
                            {{ user }}
                        </span>
                    </div>
                </div>
            </div>
        </template>
    </slide-over-panel>

    <!-- Archive confirmation. A modal rather than a slide-over: this is a
         single irreversible yes/no, not a form.

         Hand-rolled rather than NotificationModal because that component
         renders no backdrop, so the page behind stays fully lit and the dialog
         reads as floating rather than modal. Teleported so no parent can clip
         it, and above the drawer's z-index so it cannot land behind one.

         Gated on `useTemplateCards`; unported reports keep the slide-over. -->
    <archive-group-report-modal v-if="useTemplateCards"
                                :show="archiveTarget !== null"
                                :name="archiveTarget?.name ?? ''"
                                :busy="archiving"
                                @update:show="(open) => { if (! open) archiveTarget = null }"
                                @confirm="archiveGroupReport"
    ></archive-group-report-modal>

    <default-settings-panel v-if="useTemplateCards && schema"
                            v-model:show="showDefaultSettings"
                            :schema="schema"
                            :templates-api="templatesApi"
                            :default-settings="defaultSettings"
                            :prepare-settings="prepareSettingsFunction"
                            :users="users"
                            :timezone="company.timezone"
                            :context="groupReportContext"
                            :can-edit="permissions.can('edit_report_templates') && canEdit"
                            @saved="notify('Default settings saved')"
    ></default-settings-panel>

    <!-- Panel for Archive Report, for reports still on the table layout. -->
    <slide-over-panel   v-if="! useTemplateCards"
                        v-model:show="showArchiveTemplatePanel"
                        z-index="z-40"
                        mobile-bottom-sheet>
        <template   #title>
            Archive Report Template: {{ $data._templateName }}
        </template>

        <template   #content>
            <div class="rounded-md bg-red-50 p-4">
                <div class="flex">
                    <div class="flex-shrink-0">
                        <tabler-icon name="circleX" size-class="h-5 w-5 text-red-400"></tabler-icon>
                    </div>
                    <div class="ml-3">
                        <span class="text-sm font-medium text-red-800">Archiving Template</span>
                        <div class="mt-2 text-lg red">
                            <p>
                                Archiving this template will <b>permanently</b> remove it from the list of available templates. You will be able to view the previously run
                                reports on the Report History page.
                            </p>
                        </div>
                    </div>
                </div>
            </div>


            <!-- Save Template Button -->
            <div    v-if="canEdit"
                    class="flex justify-center">
                <button v-if="isSaving"
                        class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-red-200 text-md leading-5 font-sm
                            font-sans rounded-md text-red-700 bg-white rounded opacity-50 cursor-not-allowed">
                    <tabler-icon name="loader" size-class="h-4 w-4 animate-spin mr-3"></tabler-icon>
                    Archiving Report Template, Please Wait
                </button>
                <button v-else
                        class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-red-200 text-md leading-5 font-sm
                            font-sans rounded-md text-red-700 bg-white hover:bg-red-600 hover:text-white
                            focus:outline-none focus:ring-red-200 focus:border-red-400 active:bg-red-600"
                        @click="archiveReport($data._currentReportTemplateIndex)">
                    Archive Report Template
                </button>
            </div>
        </template>
    </slide-over-panel>

    <!-- Settings drawer for the manual-run view (ENGS-677). Opt-in via
         `use-settings-drawer`, so reports still using the right-column layout
         are unaffected. Hosts the same #edit-settings slot. -->
    <slide-over-panel   v-if="useSettingsDrawer"
                        v-model:show="showSettingsPanel"
                        z-index="z-40"
                        mobile-bottom-sheet>
        <template   #title>
            Report Settings
        </template>

        <template   #content>
            <!-- Bottom padding clears the fixed footer so the last setting is
                 never hidden behind it. -->
            <div :class="canSaveFromSettingsDrawer ? 'pb-28' : 'pb-8'">
                <slot   name="edit-settings"></slot>
            </div>

            <!-- Save stays reachable no matter how long the settings list gets.
                 Fixed rather than sticky: SlideOverPanel positions its content
                 absolutely inside a scrolling parent, where sticky does not
                 stick. Width is pinned to the drawer's own width. -->
            <div    v-if="canSaveFromSettingsDrawer"
                    class="fixed bottom-0 right-0 z-10 w-screen max-w-2xl border-t border-gray-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6"
            >
                <button :disabled="isSaving"
                        class="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 hover:bg-blue-500 focus:border-blue-700 focus:outline-none active:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-600"
                        @click="saveSettings($data._currentReportTemplateIndex)"
                >
                    <tabler-icon   v-if="isSaving" name="loader" size-class="h-4 w-4 animate-spin mr-3"></tabler-icon>
                    {{ isSaving ? 'Saving Settings, Please Wait' : 'Save Settings' }}
                </button>
            </div>
        </template>
    </slide-over-panel>

    <!-- Panel for Run report -->
    <slide-over-panel   v-model:show="showIntervalSelectionPanel"
                        z-index="z-40"
                        mobile-bottom-sheet>
        <template   #title>
            Select Report Interval
        </template>

        <template   #content>
            <!-- Who this run reaches, stated before the form rather than as a
                 footnote after it. Running for a group emails everyone in it,
                 which is worth knowing before you fill anything in. -->
            <div    v-if="useTemplateCards && $data._forGroup"
                    class="mb-4 flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-3">
                <tabler-icon    name="users"
                                size-class="h-5 w-5"
                                class="mt-0.5 text-blue-600"
                ></tabler-icon>

                <div class="text-sm">
                    <p class="m-0 font-medium text-blue-900">
                        This report will be sent to the whole group
                    </p>
                    <p class="m-0 mt-0.5 text-blue-800">
                        Every member of {{ groupName ? `"${groupName}"` : 'this group' }} will receive a copy by email.
                    </p>
                </div>
            </div>

            <!-- pb-24 clears the fixed panel footer so the last field is never
                 hidden behind the Generate Report bar. -->
            <div    v-if="showIntervalSelectionPanel"
                    class="pb-24">
                <!-- The template id is passed straight down rather than read
                     back off the parent's `currentReportTemplateId`: that one
                     arrives via an emit a tick later, so the slot would render
                     its first frame believing this was an On-Demand run. -->
                <slot   :group-id="$data._groupId"
                        :report-template-id="$data._currentReportTemplateId"
                        :footer-mode="'panel'"
                        name="run-options"
                ></slot>
            </div>

            <!-- Original footnote, for reports still on the table layout. -->
            <div    v-if="! useTemplateCards && $data._forGroup"
                    class="text-gray-400 flex justify-around w-full">
                <span   class="italic">Report will be sent to all members of group.</span>
            </div>
        </template>
    </slide-over-panel>

    <!-- Submitting a report used to open a spinner modal, then stack a second
         "Report Processing" modal on top of it before the first closed
         underneath. Both states are now reported inline: the Generate button
         shows its own spinner, and the outcome appears as a banner above the
         form. Nothing blocks the page. -->

    <div class="mx-auto max-w-[100rem] sm:px-6 lg:px-8">
        <!-- A successful run, as the top toast from the report designs. First
             in the content column so it centres over the report, not the
             window. -->
        <alert-toast    v-model:show="$data._ranReport"
                        title="Success"
        >
            This report will be emailed to {{ runRecipient }}.
        </alert-toast>

        <!-- A failed run, the same toast in red. Stays until closed: the user
             needs to read it. -->
        <alert-toast    v-model:show="showRunError"
                        type="error"
                        title="Error"
                        :duration="0"
        >
            {{ runErrorText }}
        </alert-toast>

        <!-- A saved, archived or updated group report (or defaults). -->
        <alert-toast    v-model:show="showNotice"
                        title="Success"
        >
            {{ noticeText }}
        </alert-toast>

        <alert-toast    v-model:show="showGroupReportError"
                        type="error"
                        title="Error"
                        :duration="0"
        >
            {{ groupReportError }}
        </alert-toast>

        <!-- A group report's own New / Edit page. -->
        <group-report-form  v-if="showGroupReportForm"
                            :schema="schema"
                            :template-id="templateId"
                            :templates-api="templatesApi"
                            :default-settings="defaultSettings"
                            :prepare-settings="prepareSettingsFunction"
                            :list-url="groupReportsListUrl"
                            :history-url="groupReportHistoryUrl"
                            :manage-group-url="groupSettingsUrl"
                            :timezone="company.timezone"
                            :context="groupReportContext"
                            :can-edit="templateId === 'new' ? canCreateGroupReport : (permissions.can('edit_report_templates') && canEdit)"
                            :can-archive="permissions.can('delete_report_templates') && canEdit"
                            :can-view-history="permissions.can('view_report_history')"
                            :can-run="true"
                            @run="openGroupReportRun($event.template, $event.forGroup)"
        >
            <template   #icon>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" v-html="reportIconSvg"></svg>
            </template>
        </group-report-form>

        <template v-else>

        <!-- The report-title slot (a TitleNav) is the page header: it renders
             ALWAYS, from the first paint, so it is the stable anchor at the top of
             the page rather than something that flashes in once async state (the
             feature level / display section) resolves. It is rendered identically
             in both modes, so it does not shift when toggling On-Demand <-> Group
             Templates. The slot is handed `open-help` / `show-help` so the host can
             render its own header actions (e.g. "How it works") in TitleNav's
             #actions slot, wired to THIS component's help panel state -- TitleNav
             itself knows nothing about help. -->
        <!-- A report with a schema gets the shared header from it, so every
             ported report has the same title, info icon and actions without
             passing its own. The run action sits here rather than in a footer
             on the card layout. Reports without a schema keep their slot. -->
        <report-page-header v-if="schema"
                            class="mb-6"
                            :title="schema.title"
                            :description="schema.description"
                            :info="schema.info"
        >
            <template   #icon>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" v-html="reportIconSvg"></svg>
            </template>

            <template   #actions>
                <template v-if="useTemplateCards && showDisplaySectionTemplate && showTemplateAffordances">
                    <button type="button"
                            class="inline-flex shrink-0 items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium leading-5 text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            @click="showDefaultSettings = true"
                    >
                        Default Settings
                        <tabler-icon    name="layoutSidebarRight"
                                        size-class="h-4 w-4"
                        ></tabler-icon>
                    </button>

                    <a  v-if="canCreateGroupReport"
                        :href="newGroupReportUrl"
                        class="inline-flex shrink-0 items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 hover:bg-blue-500 hover:text-white hover:no-underline"
                    >New Group Report</a>
                </template>


                <template   v-if="useCardLayout && showDisplaySectionManual && ! showNoConfigurationNeeded">
                    <!-- No group reporting means no template to protect, so the
                         On-Demand settings save as the report's defaults. -->
                    <button v-if="canSaveFromSettingsDrawer"
                            type="button"
                            :disabled="isSaving"
                            class="inline-flex items-center gap-2 rounded-md border border-blue-600 bg-white px-4 py-2 text-sm font-medium leading-5 text-blue-700 transition-colors duration-150 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                            @click="saveSettings($data._currentReportTemplateIndex)"
                    >
                        <tabler-icon   v-if="isSaving" name="loader" size-class="h-4 w-4 animate-spin"></tabler-icon>
                        {{ isSaving ? 'Saving Settings' : 'Save Settings' }}
                    </button>

                    <button type="button"
                            :disabled="! reportRun.state?.canRun"
                            :title="reportRun.state?.disabledReason ?? ''"
                            class="inline-flex items-center gap-2 rounded-md border border-transparent bg-green-500 px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-green-500"
                            @click="reportRun.run && reportRun.run()"
                    >
                        <tabler-icon   v-if="reportRun.state?.isRunning" name="loader" size-class="h-4 w-4 animate-spin"></tabler-icon>
                        <tabler-icon    v-else
                                        name="chartBar"
                                        size-class="h-4 w-4"
                        ></tabler-icon>
                        Run Report
                    </button>
                </template>
            </template>
        </report-page-header>

        <slot   v-else
                name="report-title"
                :open-help="() => (showHelpPanel = true)"
                :show-help="useTemplateCards && showDisplaySectionTemplate"
        ></slot>

        <div>
            <!-- Header: the back button + On-Demand/Group-Templates switcher +
                 settings trigger. The report title (TitleNav) is the stable
                 anchor slot rendered above; it is NOT repeated here. Shows
                 whenever we are past the mode modal -- for group-reporting
                 companies that is after they pick a mode; for non-group companies
                 (decoupled path) that is immediately, since they land straight on
                 the on-demand run form with no modal to return to. -->
            <div    v-if="showReportHeader"
                    class="flex flex-col w-full gap-6 mb-8"
                    :class="useCardLayout ? 'items-center' : 'items-start'"
            >
                <!-- A report with two modes uses the switcher below to move between
                     them: group reporting (On-Demand vs Group Templates) or an
                     automatic-send report (On-Demand vs Automatic). A plain
                     on-demand-only report has neither, so it gets a link out to the
                     reports index instead. -->
                <a  v-if="! showTemplateAffordances && ! showAutomaticAffordance && ! useCardLayout"
                    :href="company.buildCompanyRoute('reports')"
                    class="self-start flex text-nowrap w-fit items-center justify-center bg-transparent px-0 py-1 text-sm font-medium leading-5 text-gray-700 hover:text-gray-900 hover:no-underline focus:text-gray-900 focus:no-underline plain-link"
                >
                    <tabler-icon name="arrowLeft" size-class="h-4 w-4 mr-2"></tabler-icon> Back to All Reports
                </a>

                <!-- Single control for switching between On-Demand and Group
                     Templates, replacing the old selection modal. Only shown when
                     the report actually has both modes. The settings entry point
                     is NOT here: it lives on the Report Settings card in the run
                     form ("Configure"), so the header carries only the switch. -->
                <segmented-toggle   v-if="showTemplateAffordances"
                                    :model-value="displaySection"
                                    :on-value="SHOW_MANUAL_VALUE"
                                    :off-value="SHOW_TEMPLATE_VALUE"
                                    on-label="On Demand"
                                    off-label="Group Reports"
                                    aria-label="Report mode"
                                    @update:model-value="selectDisplaySection"
                ></segmented-toggle>

                <!-- Automatic-send reports (e.g. calibration on a non-group
                     company) switch between running now and a scheduled recurring
                     send. This is the path the chooser modal's On-Demand vs
                     Automatic option used to cover. -->
                <segmented-toggle   v-else-if="showAutomaticAffordance"
                                    :model-value="displaySection"
                                    :on-value="SHOW_MANUAL_VALUE"
                                    :off-value="SHOW_AUTOMATIC_VALUE"
                                    on-label="On Demand"
                                    off-label="Automatic"
                                    aria-label="Report mode"
                                    @update:model-value="selectDisplaySection"
                ></segmented-toggle>

                <!-- A short description of the currently selected mode, moved out
                     of the old selection modal so the same guidance is available
                     inline. Only meaningful when there are two modes to switch. -->
                <p  v-if="(showTemplateAffordances || showAutomaticAffordance) && ! useCardLayout"
                    class="m-0 text-left text-sm text-gray-500">{{ modeDescription }}</p>
            </div>

            <!-- Report Name -->
            <div    v-if="showDisplaySectionManual && ! hideTitle">
                <h2 class="p-0 mt-0">{{ prettyReportName }}</h2>
            </div>
        </div>

        <!-- Manual Run, card layout (every ported report): the run form and the
             report's settings are cards in one grid, and the run button is in the
             header above. -->
        <div    v-if="displaySection === 'show-manual' && useCardLayout"
                class="pb-12">
            <report-card-grid   :run="reportRun"
                                :single-column="! hasRunInputs"
                                :has-settings="hasSettings"
                                :centered="hasRunInputs && ! hasSettings && reportRun.state?.hasPointsCard === false"
            >
                <slot   :group-id="$data._groupId"
                        :report-template-id="null"
                        :footer-mode="'none'"
                        name="run-options"
                ></slot>

                <slot   v-if="hasSettings"
                        name="edit-settings"
                ></slot>

                <!-- Run-and-go reports have nothing to pick: say so, with the
                     run action right there. -->
                <div    v-if="showNoConfigurationNeeded"
                        class="col-span-full flex flex-col items-center gap-4 py-12 text-center">
                    <tabler-icon    name="ban"
                                    size-class="h-24 w-24 text-gray-400"
                                    :stroke-width="1.25"
                    ></tabler-icon>
                    <p  class="m-0 text-lg text-gray-500"
                        :title="noRunInputsMessage">No Configuration Needed</p>

                    <!-- With nothing to set, the run action sits here rather
                         than in the header. -->
                    <button type="button"
                            :disabled="! reportRun.state?.canRun"
                            class="inline-flex items-center gap-2 rounded-md border border-transparent bg-green-500 px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-green-500"
                            @click="reportRun.run && reportRun.run()"
                    >
                        <tabler-icon    v-if="reportRun.state?.isRunning"
                                        name="loader"
                                        size-class="h-4 w-4 animate-spin"
                        ></tabler-icon>
                        <tabler-icon    v-else
                                        name="chartBar"
                                        size-class="h-4 w-4"
                        ></tabler-icon>
                        Run Report
                    </button>
                </div>
            </report-card-grid>
        </div>

        <!-- Manual Run. pb-24 leaves room BELOW the card (not inside it) so the
             card ends right after its content while the page can still scroll clear
             of the fixed Generate Report footer. -->
        <div    v-else-if="displaySection === 'show-manual'"
                class="pb-24">
            <!-- With the drawer in use the right column has no content, so the
                 form gets the full card width instead of a third of it. -->
            <base-report :hide-right-column="hideRightColumn || useSettingsDrawer">
                <!-- Report Settings: the top section of the run card, so the
                     settings summary and the run inputs read as one continuous
                     surface rather than two stacked boxes. It renders in
                     BaseReport's default slot (above the columns) with a bottom
                     divider that matches the divide-y rows in the form below.

                     It is a persistent, always-visible summary of the current
                     report options -- the chips mirror the template cards'
                     "what's enabled" list (enabledFeatureLabels) -- and the entry
                     point to change them: "Configure" opens the same slide-over
                     the header button used to. The settings stay OFF the form
                     itself (Reporting Period / Monitoring Points), so those remain
                     a clean list of run inputs.

                     Only on the drawer-based card layout (showSettingsTrigger
                     folds in "manual view + report has settings"), so nothing
                     renders for reports without configurable manual settings. -->
                <!-- The bottom divider separates the settings summary from the run
                     inputs below it. Run-and-go reports have no inputs (just the
                     fixed footer), so they drop the divider to avoid a rule over
                     empty space. -->
                <div    v-if="showSettingsTrigger"
                        class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
                        :class="hasRunInputs ? 'mb-5 border-b border-gray-200 pb-5' : ''">
                    <div class="min-w-0">
                        <h3 class="m-0 block p-0 text-left text-base font-semibold text-gray-900">
                            Report Settings
                        </h3>
                        <p class="m-0 mt-0.5 text-sm text-gray-500">
                            Choose what appears in the generated report.
                        </p>

                        <!-- A live preview of what the run will contain. When
                             nothing content-adding is enabled, say so plainly
                             rather than showing an empty row. -->
                        <div    v-if="settingsSummaryLabels.length > 0"
                                class="mt-2 flex flex-wrap gap-1.5">
                            <span   v-for="label in settingsSummaryLabels"
                                    :key="label"
                                    class="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
                                {{ label }}
                            </span>
                        </div>
                        <p  v-else
                            class="m-0 mt-2 text-sm text-gray-500">
                            Using the report's default options.
                        </p>
                    </div>

                    <!-- The button is a full 48px (~8mm) tap target and full width
                         on a phone, shrinking to a natural inline size from sm up --
                         matching the other mobile controls on this page. Colours
                         match ButtonBlueSecondary (blue-300 border, blue-700 text). -->
                    <button type="button"
                            class="inline-flex min-h-[48px] w-full shrink-0 items-center justify-center gap-2 rounded-md border border-blue-300 bg-white px-4 py-2 text-sm font-medium leading-5 text-blue-700 transition-colors duration-150 hover:border-transparent hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:min-h-0 sm:w-auto"
                            @click="showSettingsPanel = true"
                    >
                        <tabler-icon name="settings" size-class="h-4 w-4"></tabler-icon>
                        Configure
                    </button>
                </div>

                <!-- Run-and-go reports have neither a settings summary
                     (showSettingsTrigger, false when the schema has no manual
                     fields) nor in-body run inputs (hasRunInputs), which left the
                     card rendering as an empty bordered box: the only run
                     affordance is the fixed footer, which sits outside the card.
                     Explain that there is nothing to configure rather than show a
                     blank surface. -->
                <div    v-if="showNoConfigurationNeeded"
                        class="py-2 text-left">
                    <h3 class="m-0 block p-0 text-base font-semibold text-gray-900">
                        No configuration needed
                    </h3>
                    <p class="m-0 mt-1 block p-0 text-sm leading-5 text-gray-500">
                        {{ noRunInputsMessage }}
                    </p>
                </div>

                <!-- Left Column -->
                <template   #left-column>
                    <div>
                        <!-- The inline form is always an On-Demand run, so it
                             passes no template id. The slide-over passes the
                             saved template's id instead. -->
                        <slot   :group-id="$data._groupId"
                                :report-template-id="null"
                                :footer-mode="'page'"
                                name="run-options"
                        ></slot>
                    </div>
                </template>

                <!-- Right Column -->
                <template   #right-column>
                    <!-- With the drawer in use the settings live there instead,
                         so they are not repeated in the column. -->
                    <slot   v-if="! useSettingsDrawer"
                            name="edit-settings">
                    </slot>

                    <!-- Save Template Button -->
                    <!-- TODO. Fix Permission -->
                    <div    v-if="! groupReportingEnabled && canManageExcursionReport && (this.reportName !== 'nabp-report' || this.reportName !== 'mkt-report')"
                            class="flex flex-col justify-center">
                        <!-- Subscribed Users -->
                        <div class="mb-4">
                            <user-select-input  v-model="$data._settings['subscribedUsers']"
                                                :users="users"
                                                label="Users to Send to Automatically"
                                                modal-title="Choose recipients"
                                                modal-description="They will be emailed this report each time it runs on schedule."
                                                choose-label="Choose recipients"
                                                change-label="Change recipients"
                                                empty-summary="No users selected"
                                                empty-message="No users are available to receive this report."
                                                noun="user"
                                                plural-noun="users"
                            ></user-select-input>
                        </div>

                        <div class="w-full flex justify-center">
                            <!-- Saving settings indicator -->
                            <button v-if="isSaving"
                                    class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white rounded opacity-50 cursor-not-allowed">
                                <tabler-icon name="loader" size-class="h-4 w-4 animate-spin mr-3"></tabler-icon>
                                Saving Settings, Please Wait
                            </button>

                            <!-- Save Settings Button -->
                            <button v-else
                                    class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white hover:bg-blue-600 hover:text-white
                            focus:outline-none focus:ring-blue-200 focus:border-blue-400 active:bg-blue-600"
                                    @click="saveSettings($data._currentReportTemplateIndex)">
                                Save Settings
                            </button>
                        </div>
                    </div>
                </template>
            </base-report>
        </div>

        <!-- Automatic (non-group): recipients only. -->
        <div    v-if="showDisplaySectionAutomatic" class="w-full">
            <slot name="automatic-options"></slot>
        </div>

        <!-- Group Reports tab (ENGS-677): this report's group reports as a
             table. Each row links to its own Edit page. -->
        <group-report-list  v-if="useTemplateCards && showTemplateAffordances && displaySection === 'show-template'"
                            v-model:search="templatesApi.search.value"
                            :templates="templatesApi.templates.value"
                            :schema="schema"
                            :loading="templatesApi.loading.value"
                            :edit-url="editGroupReportUrl"
                            :new-url="newGroupReportUrl"
                            :can-create="canCreateGroupReport"
                            :can-run="true"
                            :can-archive="permissions.can('delete_report_templates')"
                            :can-view-history="permissions.can('view_report_history')"
                            @run="openGroupReportRun($event.template, $event.forGroup)"
                            @history="openGroupReportHistory($event.id)"
                            @archive="archiveTarget = $event"
        ></group-report-list>

        <!-- Template Page -->
        <div    v-if="! useTemplateCards && showTemplateAffordances && displaySection === 'show-template'"
                class="flex justify-center w-full">
            <div class="w-full">
                <h2> Saved Report Templates for {{ this.prettyReportName }}</h2>
                <ajax-table     v-model:items-per-page="ajaxTable.itemsPerPage"
                                v-model:loading="ajaxTable.loading"
                                v-model:page="ajaxTable.page"
                                v-model:page-info="ajaxTable.pageInfo"
                                v-model:search="ajaxTable.search"
                                v-model:sort-by="ajaxTable.sortBy"
                                v-model:sort-desc="ajaxTable.sortDesc"
                                v-model:selected-row="$data._currentReportTemplateIndex"
                                :row-selectable="false"
                                :allow-highlighted-rows="true"
                                :use-group-table-styling="true"
                                :data="$data._data"
                                :exportable="false"
                                :headers="headers"
                                @loadData="loadTemplateData"
                >
                    <!-- Help Button -->
                    <template   #search-right="props">
                        <div    class="flex items-center ml-2 mb-4 p-1 rounded-full border border-blue-700 cursor-pointer"
                                @click="showHelpPanel = true">
                            <tabler-icon name="help" size-class="h-4 w-4 text-blue-700"></tabler-icon>
                        </div>
                    </template>

                    <!-- Group Row -->
                    <template   #group-row="props">
                        <div    class="w-full flex justify-between items-center bg-gradient-to-r from-gray-50 to-white py-4 border-t border-gray-300">
                            <div    class="px-6 py-2 flex items-center gap-3 flex-1"
                            >
                                <tabler-icon name="users" size-class="h-5 w-5 text-green-600"></tabler-icon>

                                <span   class="px-4 py-2 rounded-full bg-green-100 text-green-800 text-sm font-semibold">
                                    {{ props.row['groupName'] }}
                                </span>

                                <div    v-if="! props.row.canView"
                                        class="ml-2">
                                    <tooltip-container  :tooltip-text="`You can't normally view this group, so you won't receive reports from these report templates.`"
                                                        width="250">
                                        <tabler-icon name="eyeOff" size-class="h-4 w-4 text-red-600"></tabler-icon>
                                    </tooltip-container>
                                </div>

                                <span class="ml-auto text-sm text-gray-500 font-normal italic">
                                    {{ getTemplateCountForGroup(props.row.groupId) }} template{{ getTemplateCountForGroup(props.row.groupId) !== 1 ? 's' : '' }}
                                </span>
                            </div>
                            <div    v-if="canEdit && props.row.groupId !== 0"
                                    class="pr-6">
                                <button-green-primary @click="createReportTemplate($data._data[props.rowIndex])" class="my-0 whitespace-nowrap">
                                    <tabler-icon name="circlePlus" size-class="h-4 w-4 mr-2"></tabler-icon>
                                    Create Template
                                </button-green-primary>
                            </div>
                        </div>
                    </template>

                    <!-- Interval -->
                    <template   #col-2="props"
                                @click.stop>
                        <span>{{ formatInterval(props.row.report_duration_type) }}</span>
                        <span   v-if="props.row.report_duration_type === 'weekly'"
                                class="pl-1">
                            ({{ weekdayName(props.row.report_settings.weekday) }})
                        </span>
                    </template>

                    <!-- Actions to take on the report template. -->
                    <template   #col-3="props">
                        <div    class="w-full flex justify-end overflow-visible"
                                @click.stop>
                            <!-- Headless UI -->
                            <Menu as="div" class="inline-block text-left">
                                <div>
                                    <MenuButton class="inline-flex w-full justify-end rounded-md border border-gray-300 bg-white pl-4 pr-0.5 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                                        Action
                                        <div    class="h-5 w-5 mx-2">
                                            <span   :class="`${iconSortDesc} !text-sm`"></span>
                                        </div>
                                    </MenuButton>
                                </div>

                                <MenuItems class="absolute mt-2 w-56 divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                                           style="">
                                    <div class="py-1">
                                        <MenuItem v-slot="{ active }">
                                        <span   :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm']"
                                                @click="setReportTemplate(props.rowIndex)"
                                                class="cursor-default"
                                        >
                                            <span    class="h-6 w-6 mr-1">
                                                <tabler-icon name="chevronRight" size-class="h-4 w-4"></tabler-icon>
                                            </span>
                                            Run for me
                                        </span>
                                        </MenuItem>
                                        <MenuItem   v-slot="{ active }">
                                            <span   :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm']"
                                                    @click="setReportTemplate(props.rowIndex, true)"
                                                    class="cursor-default"
                                            >
                                                <span    class="h-6 w-6 mr-1">
                                                    <tabler-icon name="send" size-class="h-4 w-4"></tabler-icon>
                                                </span>
                                                Run for group
                                            </span>
                                        </MenuItem>
                                            <MenuItem   v-if="permissions.can('view_report_history')"
                                                        v-slot="{ active }"
                                                        class="cursor-default"
                                            >
                                            <span   :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm']"
                                                    @click="viewReportHistory(props.rowIndex)"
                                            >
                                                <span    class="h-6 w-6 mr-1">
                                                    <tabler-icon name="history" size-class="h-4 w-4"></tabler-icon>
                                                </span>
                                                View history
                                            </span>
                                        </MenuItem>
                                        <MenuItem   v-if="permissions.can('edit_report_templates')"
                                                    v-slot="{ active }"
                                                    class="cursor-default"
                                        >
                                        <span   :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm']"
                                                @click="editReportTemplate(props.rowIndex)">
                                            <span    class="h-6 w-6 mr-1">
                                                <tabler-icon name="pencil" size-class="h-4 w-4"></tabler-icon>
                                            </span>
                                            Edit report
                                        </span>
                                        </MenuItem>
                                        <MenuItem   v-if="permissions.can('delete_report_templates')"
                                                    v-slot="{ active }"
                                                    class="cursor-default"
                                        >
                                        <span   :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm']"
                                                @click="retireReportTemplate(props.rowIndex)">
                                            <span    class="h-6 w-6 mr-1">
                                                <tabler-icon name="archive" size-class="h-4 w-4"></tabler-icon>
                                            </span>
                                            Archive report
                                        </span>
                                        </MenuItem>
                                    </div>
                                </MenuItems>
                            </Menu>
                        </div>
                    </template>
                </ajax-table>

                <div    class="mb-16"></div>
            </div>
        </div>
        </template>
    </div>
</template>

<script>
import axios from "axios"
import {SonicuCoreMixin} from "../../mixins/SonicuCoreMixin";
import {ajaxTableMixin} from "../../mixins/ajaxTableMixin";
import SlideOverPanel from "../elements/SlideOverPanel";
import Select2 from "../elements/select2";
import ButtonBlueSecondary from "../elements/buttons/ButtonBlueSecondary";
import SegmentedToggle from "../elements/SegmentedToggle.vue";
import BaseReport from "../Reports/BaseReport";
import AjaxTable from "../elements/AjaxTable";
import ButtonGreenPrimary from "../elements/buttons/ButtonGreenPrimary";
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/vue";
import {Intervals} from "./Intervals";
import {iconNamesMixin} from "../../mixins/IconNamesMixin";
import NotificationModal from "../elements/NotificationModal";
import {Features} from "../../mixins/AuthorizationHelpers/Features";
import _ from "lodash";
import FloatingInput from "../elements/FloatingInput.vue";
import TooltipContainer from "@/components/elements/TooltipContainer.vue";
import {UrlParamsMixin} from "@/mixins/UrlParamsMixin";
import UserSelectInput from "@/components/elements/UserSelectInput.vue";
import GroupReportList from "./settings/GroupReportList.vue";
import GroupReportForm, {GROUP_REPORT_TOAST_KEY} from "./settings/GroupReportForm.vue";
import DefaultSettingsPanel from "./settings/DefaultSettingsPanel.vue";
import ArchiveGroupReportModal from "./settings/ArchiveGroupReportModal.vue";
import {useReportTemplates} from "@/composables/useReportTemplates";
import {markRaw} from "vue";
import {enabledFeatureLabels} from "./settings/enabledFeatures";
import {DEFAULT_REPORT_ICON} from "./settings/reportIcons";
import TablerIcon from "@/components/elements/TablerIcon.vue";
import ReportCardGrid from "./ReportCardGrid.vue";
import {GENERIC_RUN_ERROR} from "@/composables/useGroupReport";
import AlertToast from "@/components/elements/AlertToast.vue";
import ReportPageHeader from "./settings/ReportPageHeader.vue";

const SHOW_MANUAL = 'show-manual'
const SHOW_TEMPLATE = 'show-template'
const SHOW_AUTOMATIC = 'show-automatic'

const REPORT_TEMPLATE_NAMES = {
    'detailed-alarm-report': 'Detailed Alarm Report',
    'alarm-resolution-report': 'Alarm Resolution Report',
    'excursion-report' : 'Excursion Report',
    'room-condition-report' : 'Room Condition Report',
    'nabp-report': 'MKT Report',
    'mkt-report': 'MKT Report',
    'weekly-device-summary-report': "Device Summary Report",
    'threshold-achievement-report': 'Threshold Achievement Report',
    'system-health-report': 'System Health Report',
    'occupancy-report': 'Occupancy Report',
    'temperature-interval-breakdown-report' : 'Temperature Interval Breakdown Report',
    'calibration-report': 'Calibration Report',
    'alarm-configuration-report': 'Alarm Configuration Report',
    'sound-target-achievement-report': 'Sound Target Achievement Report (S.T.A.R.)',
    'star-graph-addendum': 'S.T.A.R. Graph Addendum',
    'ryobi-oil-usage': 'Oil Usage Report',
}

// A run error fit to show a user: prose from the server as-is, anything that
// looks like machine output (HTML, JSON, stderr, stack traces, paths, or just
// implausibly long) as the generic message. The original is logged.
const presentableRunError = (message) => {
    const text = typeof message === 'string' ? message.trim() : ''
    const looksLikeInternals = text === ''
        || text.startsWith('<')
        || text.startsWith('{')
        || text.length > 160
        || /stderr|stdout|stack trace|exit status|exit code|Warning:|Error:|Exception|\[=+|\/var\/www|node_modules|\bat [A-Z]\w+\./i.test(text)

    if (looksLikeInternals && text !== '') {
        console.error('Report run failed', text)
    }

    return looksLikeInternals ? GENERIC_RUN_ERROR : text
}

export default {
    name: "GroupReportTemplate",
    mixins: [SonicuCoreMixin, ajaxTableMixin, iconNamesMixin, UrlParamsMixin],
    emit: ['update:runningReport', 'update:runningErrors', 'update:displaySection', 'useStandardReporting', 'resetSettings'],
    components: {
        AlertToast,
        ReportCardGrid,
        ReportPageHeader,
        UserSelectInput,
        GroupReportList,
        GroupReportForm,
        DefaultSettingsPanel,
        ArchiveGroupReportModal,
        TablerIcon,
        TooltipContainer,
        FloatingInput,
        NotificationModal,
        Select2,
        MenuItem,
        MenuItems,
        Menu,
        MenuButton,
        ButtonGreenPrimary, AjaxTable, BaseReport, SegmentedToggle, ButtonBlueSecondary, SlideOverPanel},
    props: {
        // null on the report page; 'new' or a group report id on its New or
        // Edit page (see ResolveReportTemplateForm).
        templateId: {
            type: [String, Number],
            default: null,
        },
        interval: {
            type: String,
            default: 'monthly',
        },
        settings: {
            type: Object,
            default() {
                return {}
            }
        },
        meters: {
            type: Object,
            default() {
                return {}
            },
        },
        forGroup: {
            type: Boolean,
            default: false,
        },
        reportName: {
            type: String,
            default: 'detailed-alarm-report',
        },
        // Whether this company has been rolled onto group reporting, i.e. the
        // `use-group-reporting` option (NOT the plan's GROUP_REPORTING feature --
        // those diverge: most companies have the feature but have not been rolled
        // on). Drives whether the scheduled/template layer + mode modal show, or
        // the page goes straight to the on-demand run form.
        //
        // Three states: `undefined` = the host page does not pass it (legacy
        // pages fall back to the GROUP_REPORTING feature check); `null` = passed
        // but still loading async from the composable; `true`/`false` = resolved.
        useGroupReportingOption: {
            type: Boolean,
            default: undefined,
        },
        // Per-report override: when true this report is on-demand-only for EVERY
        // company, group reporting on or off. It suppresses the mode modal and the
        // template/scheduled section for THIS report only. Distinct from the
        // company-level useGroupReportingOption: a group-reporting company still
        // gets templates on its other reports; only reports passing this prop skip
        // them. Default false keeps every already-ported report unchanged.
        onDemandOnly: {
            type: Boolean,
            default: false,
        },
        // The report has a non-group AUTOMATIC send (a scheduled email to a recipient
        // list), distinct from group-reporting templates. Only meaningful together
        // with onDemandOnly (a not-group-reportable report). When both are set, the
        // header switcher offers On-Demand vs Automatic. Default false keeps the
        // on-demand-only header for onDemandOnly reports with no auto-send.
        hasAutomaticSend: { type: Boolean, default: false },
        reloadData: {
            type: Boolean,
            default: false,
        },
        runningReport: {
            type: Boolean,
            default: false,
        },
        ranReport: {
            type: Boolean,
            default: false,
        },
        runningErrors: {
            type: Array,
            default() {
                return []
            },
        },
        currentReportTemplateId: {
            type: Number,
            default: 0,
        },
        defaultSettings: {
            type: Object,
            default() {
                return {}
            },
        },
        prepareSettingsFunction: {
            type: Function,
            default: null,
        },
        canEdit: {
            type: Boolean,
            default: false,
        },
        users: {
            // I am leaving this blank on the blade as well because
            // there is probably going to be some decent logic to it
            type: Object,
            default() {
                return {}
            },
        },
        displaySection: {
            type: [String, null],
            default: null
        },
        hideRightColumn: {
            type: Boolean,
            default: false
        },
        // Whether the manual run form has in-body inputs (meters, a date range,
        // an interval) below the Report Settings summary. True for normal reports;
        // false for run-and-go reports (calibration, alarm config) whose only run
        // affordance is the fixed footer, so the Report Settings summary drops its
        // bottom divider rather than leaving a rule above empty space.
        hasRunInputs: {
            type: Boolean,
            default: true
        },
        // The explanation shown in the run form when a report has neither manual
        // settings nor run inputs. Only reached when hasRunInputs is false, so
        // reports with a normal run form never need to pass it.
        noRunInputsMessage: {
            type: String,
            default: 'This report needs no configuration. Select Generate Report to run it now.'
        },
        hideTitle: {
            type: Boolean,
            default: false
        },
        // Marks a ported report. It used to move the manual-run settings into a
        // drawer; ported reports now get the card layout from the report designs
        // instead (see useCardLayout), and the name stays so no report changes.
        useSettingsDrawer: {
            type: Boolean,
            default: false,
        },
        // Render saved templates as grouped cards instead of the table, so each
        // template carries its own actions. Opt-in per report, matching how
        // `useSettingsDrawer` was introduced.
        useTemplateCards: {
            type: Boolean,
            default: false,
        },
        // The report's settings schema. Only the template-card path uses it: the
        // cards derive their "what's enabled" summary from the schema's toggle
        // fields (see enabledFeatures.js). Optional so table-mode reports that
        // never pass one keep working.
        schema: {
            type: Object,
            default: null,
        },
        // Whether the report actually has any settings to configure on a manual
        // run. A report whose only schema sections are template-only (e.g.
        // Detailed Alarm, whose sole section is `showIf: isSavable`) renders an
        // empty drawer on a manual run, so it passes false to hide the "Report
        // Settings" button entirely. Defaults true so reports that do not compute
        // this (and the ones with real manual settings) keep the button.
        hasSettings: {
            type: Boolean,
            default: true,
        },
        // Inner SVG for the active report's icon, passed by the report wrapper.
        reportIcon: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            // Group Reports tab state.
            archiveTarget: null,
            archiving: false,
            showDefaultSettings: false,
            noticeText: '',
            showNotice: false,
            groupReportError: '',
            showGroupReportError: false,
            _displaySection: this.displaySection,

            // Manual Run Stuff
            _interval: this.interval,
            endOfInterval: new Date(),
            _selectedMeters: [],
            _templateName: '',
            _groupId: 0,
            _settings: this.defaultSettings,

            // Template Run Stuff
            headers: [
                {
                    name: 'Report Template (by Group)',
                    key: 'template_name',
                    sortBy: 'reportTemplate',
                },
                {
                    name: 'Send Interval',
                    key: 'report_duration_type',
                    sortBy: 'sendInterval',
                },
                // TODO. May cause errors.
                {
                    name: '',
                    key: 'created_at',
                },
            ],
            _data: [],
            _groups: [],
            _usersByGroup: {},
            isSaving: false,

            _runningReport: this.runningReport,
            _ranReport: this.ranReport,
            _hasRunningErrors: this.runningErrors > 0,
            _runningErrors: this.runningErrors,

            _currentReportTemplateIndex: null,
            _currentReportTemplateId: this.currentReportTemplateId,
            showHelpPanel: false,
            // Shared with the card layout's run form (through ReportCardGrid):
            // the form puts its run state and run action here for the header's
            // Run Report button.
            reportRun: {state: null, run: null},
            showRunError: false,
            showSettingsPanel: false,
            showIntervalSelectionPanel: false,
            showCreateTemplatePanel: false,
            showEditTemplatePanel: false,
            showArchiveTemplatePanel: false,
            _forGroup: this.forGroup,
            _standardSettingsSaved: false,
            isFirstLoad: true,
            // Increments per template request so out-of-order responses can be
            // discarded. See loadTemplateData().
            latestTemplateRequestId: 0,
        }
    },
    created() {
        // Created rather than defined inline so every call shares one timer.
        this.debouncedLoadTemplateData = _.debounce(() => this.loadTemplateData(), 250)

        // Every report lands on the On-Demand run form. The chooser modal that
        // used to gate group-reporting reports is gone: the On-Demand <-> Group
        // Templates switcher in the header is now the single way to move between
        // the two modes, so there is no separate "pick a mode" step.
        //
        // Land in created() (before the first render), synchronously, rather than
        // in mounted() on the async feature-level listener the modal used to need.
        // primarySelection() only reads props (defaultSettings), not the feature
        // level, so deferring it left a frame where displaySection was null and
        // NOTHING rendered (the modal used to fill that gap) -- the white flash on
        // open. A reportTemplateId in the URL still wins (handled just above); this
        // is the default when no template is deep-linked.
        if (_.isNil(this.$data._displaySection)) {
            this.primarySelection()
            this.emitUseStandardReporting()
        }

        // markRaw keeps its refs as refs (a reactive wrapper would unwrap them).
        this.templatesApi = markRaw(useReportTemplates(this.reportName, (path) => this.company.buildCompanyApiInternalRoute(path)))

        // ?mode=group-reports opens the Group Reports tab, so refresh, Back and
        // a link from a group report's page land where the user was.
        const mode = new URLSearchParams(window.location.search).get('mode')
        if (mode === 'group-reports' && ! this.onDemandOnly && this.useGroupReportingOption !== false && this.useTemplateCards) {
            this.selectDisplaySection(SHOW_TEMPLATE)
        }

        // A group report page that saved or archived hands its message here.
        const notice = sessionStorage.getItem(GROUP_REPORT_TOAST_KEY)
        if (notice) {
            sessionStorage.removeItem(GROUP_REPORT_TOAST_KEY)
            this.notify(notice)
        }
    },
    mounted() {
        // The initial display section is set synchronously in created() -- every
        // report lands on the On-Demand run form now that the chooser modal is
        // gone -- so mounted() no longer decides it. The decoupled rollout option
        // still loads asynchronously, so applyGroupReportingDisplaySection() is
        // watched (see the useGroupReportingOption watcher below) to keep a
        // late-arriving option from leaving the report on the wrong section.

        if (! _.has(this.$data._settings, 'subscribedUsers')) {
            console.log(`Clear Subscribed Users`)
            this.$data._settings['subscribedUsers'] = []
        }

        // AjaxTable requests its own data on mount via @loadData. The card
        // layout has no table, so it has to ask for the data itself.
        if (this.useTemplateCards && (this.showDisplaySectionTemplate || this.showGroupReportForm)) {
            this.loadGroupReports()
        }
    },
    methods: {
        loadGroupReports() {
            // The form loads for itself; the list needs it here.
            if (this.showGroupReportForm) {
                return
            }
            this.templatesApi.load()
                .then(() => {
                    // The run panel names the group from these.
                    this.$data._groups = this.templatesApi.groups.value
                })
                .catch((error) => this.notifyError(error))
        },
        editGroupReportUrl(template) {
            return this.company.buildCompanyRoute(`group-reports/${this.groupReportsSlug}/templates/${template.id}`)
        },
        groupReportHistoryUrl(id) {
            return this.company.buildCompanyRoute(`report-history/?templateID[]=${id}`)
        },
        openGroupReportHistory(id) {
            window.open(this.groupReportHistoryUrl(id), '_blank', 'noopener')
        },
        // Run a saved group report: the run panel picks the period (and points).
        openGroupReportRun(template, forGroup = false) {
            this.$data._groups = this.templatesApi.groups.value
            this.$data._currentReportTemplateId = template.id
            this.$data._templateName = template.name
            this.$data._interval = template.interval
            this.$data._groupId = template.groupId
            this.$data._forGroup = forGroup
            this.showIntervalSelectionPanel = true
        },
        archiveGroupReport() {
            if (this.archiving || ! this.archiveTarget) {
                return
            }
            this.archiving = true

            this.templatesApi.archive(this.archiveTarget.id)
                .then(() => this.notify('Group report archived'))
                .catch((error) => this.notifyError(error))
                .finally(() => {
                    this.archiving = false
                    this.archiveTarget = null
                })
        },
        notify(message) {
            this.noticeText = message
            this.showNotice = true
        },
        notifyError(error) {
            this.groupReportError = this.templatesApi.errorMessages(error).join(' ')
            this.showGroupReportError = true
        },
        // Decoupled path: once the rollout option is known, a company NOT on
        // group reporting skips the mode modal and lands straight on the
        // on-demand run form. Only acts while still on the initial modal
        // (displaySection null) and only when the option has resolved (not null),
        // so it is safe to call from mounted and from the option's watcher.
        applyGroupReportingDisplaySection() {
            if (! _.isNil(this.$data._displaySection)) {
                return
            }
            if (this.onDemandOnly) {
                this.$data._displaySection = SHOW_MANUAL
                this.primarySelection()
                this.$emit('useStandardReporting', true)
                return
            }
            if (this.useGroupReportingOption === false) {
                this.$data._displaySection = SHOW_MANUAL
                this.primarySelection()
                this.$emit('useStandardReporting', true)
            }
        },
        /**
         * Turn whatever a failed report response produced into a list of
         * messages the error message can render. Reports assign the raw
         * response body, which is not always an array: a 500 returns an HTML
         * error page and a validation failure returns an object.
         */
        normalizeErrors(value) {
            if (_.isNil(value) || value === '') {
                return []
            }

            if (_.isArray(value)) {
                return value.filter((item) => ! _.isNil(item) && item !== '')
            }

            // Laravel validation shape: { errors: { field: [msg, ...] } }
            if (_.isObject(value) && _.isObject(value.errors)) {
                return Object.values(value.errors).flat()
            }

            if (_.isObject(value)) {
                return [value.message ?? 'The report could not be generated. Please try again.']
            }

            // A raw HTML error page is not worth showing verbatim.
            if (_.isString(value) && value.trim().startsWith('<')) {
                return ['The report could not be generated. Please try again, or contact support if it keeps failing.']
            }

            return [value]
        },
        loadTemplateData() {
            this.ajaxTable.loading = true
            // Search-as-you-type can have several requests in flight at once,
            // and they do not necessarily come back in order. Stamping each one
            // and ignoring all but the newest stops a slower earlier response
            // from overwriting the results for what was typed later.
            const requestId = ++this.latestTemplateRequestId
            let params = JSON.parse(JSON.stringify(this.ajaxTable))
            params.report_name =  this.reportName

            // If the reportTemplateId is set as part of url params, include it here.
            params.reportTemplateId =  this.urlParamsGet('reportTemplateId', null)

            axios.get(this.company.buildCompanyApiInternalRoute(`report-templates`), {
                params: params,
            }).then(response => {
                if (requestId !== this.latestTemplateRequestId) {
                    return
                }

                let data = response.data.data
                this.ajaxUpdateTableData(response.data)

                // For now we will process the data and insert new rows for the groups on the Front-End
                // This is the wrong solution in the long term, at least it shouldn't be done here.
                let newData = []

                // Add a fake group called "User-Specific (Non-Group) Reporting"
                newData.push({
                    groupId: 0,
                    groupName: 'User-Specific (Non-Group) Reporting',
                    canView: true,
                })

                // Add User-Specific (Non-Group) Reporting Template to table
                let defaultTemplate = response.data.defaultTemplate
                this.ensureReportTemplateHasDefaultSettings(defaultTemplate)
                newData.push(defaultTemplate)

                let lastGroupId = -1;
                let listedGroups = []
                _.forEach(data, (item, index) => {
                    // If we have moved to the next group, add a new group row
                    if (item.group_id > lastGroupId) {
                        newData.push({
                            groupId: item.group_id,
                            groupName: item.group_name,
                            canView: item.can_view,
                        })
                        lastGroupId = item.group_id
                        listedGroups.push(item.group_id)
                    }

                    // Make sure this report template has new default settings.
                    this.ensureReportTemplateHasDefaultSettings(item)
                    newData.push(item)
                })

                // Add Groups not included already, if there is no search
                let search = this.ajaxTable.search
                _.forEach(response.data.groups, (item, index) => {
                    if (listedGroups.indexOf(item.ID) === -1) {
                        if (search === '' || (item.GroupName.toLowerCase().indexOf(search.toLowerCase()) !== -1)) {
                            newData.push({
                                groupId: item.ID,
                                groupName: item.GroupName,
                                canView: item.can_view,
                            })
                        }
                    }
                })

                this.$data._data = newData
                this.$data._groups = response.data.groups
                this.$data._usersByGroup = response.data.usersByGroup

                // Get if there is a requested reportTemplate in the response
                let selectedReportTemplate = response.data.selectedReportTemplate
                console.log(selectedReportTemplate)
                if (! _.isNil(selectedReportTemplate)) {
                    this.updatePanelsFromUrlParams(selectedReportTemplate)
                }

                // The group list just changed, so the last group (and thus the
                // trailing spacer it needs) may be different. Remeasure once the
                // cards have rendered AND the browser has laid them out: the
                // scroll container is still growing to its final height on the
                // tick the data lands, so a bare $nextTick measures too early and
                // under-sizes the spacer. A double rAF waits for that layout pass
                // to settle before measuring.
            })
            .catch(error => {
            })
        },
        readLoadingSettingsFromUrl() {

        },
        /**
         * The last group's top offset within a scroll container, summed across
         * nested offsetParents (the group sits several wrappers deep, so a single
         * offsetTop is relative to the wrong ancestor).
         */
        offsetTopWithin(el, container) {
            let top = 0
            let node = el

            while (node && node !== container) {
                top += node.offsetTop
                node = node.offsetParent
            }

            return top
        },
        createSettings() {
            console.log(`create Settings`)
            console.log(this.$data._settings)

            console.log(`Default Settings`)
            console.log(this.defaultSettings)
            let params = {
                groupId: this.$data._groupId,
                reportName: this.reportName,
                templateName: this.$data._templateName,
                interval: this.interval,
                settings: this.$data._settings,
            }

            this.isSaving = true
            axios.post(this.company.buildCompanyApiInternalRoute(`report-templates`), params)
                .then(response => {
                    let data = response.data
                })
                .catch(response => {
                    let data = response.data
                })
                .finally(() => {
                    this.resetTemplateRun()
                    this.loadTemplateData()
                })
        },
        saveSettings(reportTemplateIndex) {
            console.log(`Saving Settings on DAR file`)

            // Log Settings
            let template = this.$data._data[reportTemplateIndex]
            console.log(`Template:`)
            console.log(template)
            let params = null
            let route = null
            if (_.isNil(template)) {
                params = this.saveSettingsStandard()
            }
            else {
                params = this.saveSettingsReportTemplate(template)
                route = template.id
            }

            this.isSaving = true
            axios.put(this.company.buildCompanyApiInternalRoute(`report-templates/${route}`), params)
                .then(response => {
                    this.$data._standardSettingsSaved = true
                })
                .catch(response => {
                })
                .finally(() => {
                    this.resetTemplateRun()
                    this.loadTemplateData()
                    this.isSaving = false
                })
        },
        archiveReport(reportTemplateIndex) {
            console.log(`Archiving Report Template`)
            let template = this.$data._data[reportTemplateIndex]

            this.isSaving = true
            axios.delete(this.company.buildCompanyApiInternalRoute(`report-templates/${template.id}`))
                .then(response => {
                })
                .catch(response => {
                })
                .finally(() => {
                    this.resetTemplateRun()
                    this.loadTemplateData()
                    this.isSaving = false
                })
        },
        saveSettingsReportTemplate(reportTemplate) {
            return {
                group_report: true,
                template_name: this.$data._templateName,
                interval: this.$data._interval,
                settings: this.$data._settings,
            }
        },
        saveSettingsStandard() {
            // Generate parameters for a standard (non-group) report.
            return {
                group_report: false,
                report_name: this.reportName,
                interval: this.$data._interval,
                settings: this.$data._settings,
            }
        },
        viewReportHistory(reportTemplateIndex) {
            let reportTemplateId = this.$data._data[reportTemplateIndex].id
            window.open(this.company.buildCompanyRoute(`report-history/?templateID[]=${reportTemplateId}`))
        },
        setReportTemplate(reportTemplateIndex, forGroup = false) {
            this.$data._currentReportTemplateIndex = reportTemplateIndex
            this.$data._currentReportTemplateId = this.$data._data[reportTemplateIndex].id

            let template = this.$data._data[reportTemplateIndex]
            this.$data._templateName = template.template_name
            this.$data._interval = template.report_duration_type
            this.$data._groupId = template.group_id
            this.$data._forGroup = forGroup

            this.showIntervalSelectionPanel = true

            this.urlParamsStore('showIntervalPanel', 'true')
            this.urlParamsStore('forGroup', forGroup ? 'true' : 'false')
        },
        editReportTemplate(reportTemplateIndex) {
            let template = this.$data._data[reportTemplateIndex]

            this.$data._templateName = template.template_name
            this.$data._interval = template.report_duration_type
            this.$data._groupId = template.group_id
            this.urlParamsStore('showEditPanel', 'true')

            // Set the log times to be dates and set the modal's settings
            let settings = template.report_settings
            if (Array.isArray(settings) || settings === '[]') {
                settings = {}
            }

            this.$data._settings = this.prepareSettings(settings)

            this.$data._currentReportTemplateIndex = reportTemplateIndex
            this.showEditTemplatePanel = true
        },
        retireReportTemplate(reportTemplateIndex) {
            let template = this.$data._data[reportTemplateIndex]

            this.$data._templateName = template.template_name

            this.$data._currentReportTemplateIndex = reportTemplateIndex
            this.showArchiveTemplatePanel = true
        },
        createReportTemplate(groupRow) {
            this.$data._templateName = 'Default Template'
            this.$data._interval = 'monthly'
            this.$data._groupId = groupRow.groupId

            this.$data._settings = this.prepareSettings(this.defaultSettings)

            this.showCreateTemplatePanel = true
        },
        resetTemplateRun() {
            this.$data._currentReportTemplateIndex = null
            this.showIntervalSelectionPanel = false
            this.showEditTemplatePanel = false
            this.showArchiveTemplatePanel = false
            this.showCreateTemplatePanel = false
            this.isSaving = false
            this.$data._forGroup = false

            this.urlParamsRemove('reportTemplateId')
            this.urlParamsRemove('showIntervalPanel')
            this.urlParamsRemove('showEditPanel')
            this.urlParamsRemove('forGroup')
        },
        ensureReportTemplateHasDefaultSettings(reportTemplate) {
            // Check if Settings Are empty and populate them w/ default settings if so.
            if (_.isEmpty(reportTemplate.report_settings)) {
                reportTemplate.report_settings = this.defaultSettings
            }

            // Check each setting w/ default settings and add if not present
            let missingSettings = _.difference(Object.keys(this.defaultSettings), Object.keys(reportTemplate.report_settings))
            _.forEach(missingSettings, (setting) => {
                reportTemplate.report_settings[setting] = this.defaultSettings[setting]
            })
        },
        primarySelection() {
            this._displaySection = SHOW_MANUAL
            this.$data._groupId = 0
            this.$emit('resetSettings')
            let settings = this.defaultSettings
            if (Array.isArray(settings) || settings === '[]') {
                settings = {}
            }

            this.$data._settings = this.prepareSettings(settings)

        },
        secondarySelection() {
            this._displaySection = SHOW_TEMPLATE
            this.$data._groupId = 0
            this.$emit('resetSettings')
        },
        automaticSelection() {
            this._displaySection = SHOW_AUTOMATIC
            this.$data._groupId = 0
            this.$emit('resetSettings')
        },
        clearSelection() {
            this._displaySection = null
        },
        selectDisplaySection(section) {
            const url = new URL(window.location.href)
            section === SHOW_TEMPLATE ? url.searchParams.set('mode', 'group-reports') : url.searchParams.delete('mode')
            history.replaceState(history.state, '', url)

            if (section === SHOW_TEMPLATE) {
                this.secondarySelection()
                return
            }

            if (section === SHOW_AUTOMATIC) {
                this.automaticSelection()
                return
            }

            this.primarySelection()
            this.emitUseStandardReporting()
        },
        /**
         * "Standard reporting" means this company has no group reporting, so the
         * On-Demand form is the only place settings can be edited and must show
         * the savable ones. When group reporting IS enabled those settings belong
         * to a Group Template, so emitting true here would surface scheduling and
         * subscriber fields on a one-off run where they do nothing.
         */
        emitUseStandardReporting() {
            this.$emit('useStandardReporting', ! this.groupReportingEnabled)

            // Feature data loads over the wire, so at created() hasFeature() is
            // still false for a company that DOES have group reporting and the
            // emit above would wrongly say "standard reporting". Re-emit once
            // the features land so the value settles on the truth.
            this.featureLevel.addListener('GroupReportTemplate.useStandardReporting', () => {
                this.$emit('useStandardReporting', ! this.groupReportingEnabled)
            })
        },
        prepareSettings(settings) {
            if (! _.isNil(this.prepareSettingsFunction)) {
                console.log(`use prepareSettingsFunction`)
                settings = this.prepareSettingsFunction(settings)
            }
            else if (_.isNil(settings)) {
                console.log(`use default Settings`)
                settings = this.defaultSettings
            }

            if (! this.isSavingDefaultTemplate) {
                console.log(`Clear subscribedUsers`)
                settings.subscribedUsers = []
            }

            return settings
        },
        formatInterval(interval) {
            return Intervals.INTERVAL_OPTIONS[interval]
        },
        weekdayName(weekday) {
            if (_.isNil(weekday)) {
                weekday = 1
            }
            return Intervals.WEEKDAY_OPTIONS[weekday]
        },
        numberOfUsersOnGroup(groupId) {
            return _.has(this.$data._usersByGroup, groupId)
                ? Object.keys(this.$data._usersByGroup[groupId]).length
                : 0
        },
        // First load code
        updatePanelsFromUrlParams(selectedReportTemplate) {
            // If this is not the first page load, do nothing.
            if (! this.isFirstLoad) {
                return
            }

            // If there is no selected report template, do nothing.
            if (_.isNil(selectedReportTemplate)) {
                return
            }

            this.$data._currentReportTemplateId = selectedReportTemplate['id']
            this.$data._currentReportTemplateIndex = this.getReportTemplateIndexById(this.$data._currentReportTemplateId)

            // set forGroup from url if it exists
            let forGroup = this.urlParamsGet('forGroup', 'false') === 'true'

            // If We should show the interval selection panel, do so.
            if (this.urlParamsGet('showIntervalPanel', 'false') === 'true') {
                this.setReportTemplate(this.$data._currentReportTemplateIndex, forGroup)
            }
            // If we should instead show the edit panel, do so.
            else if (this.urlParamsGet('showEditPanel', 'true') === 'true') {
                this.editReportTemplate(this.$data._currentReportTemplateIndex)
            }

            this.isFirstLoad = false

        },
        // Get the Report template info by its Index in _data
        getReportTemplateByIndex(index) {
            return this.$data._data[index]
        },
        // Get the Index in _data of report template by its ID
        getReportTemplateIndexById(id) {
            let foundIndex = null
            _.forEach(this.$data._data, (item, index) => {
                if (item.id === id) {
                    foundIndex = index
                    return false
                }
            })

            return foundIndex
        },
        getTemplateCountForGroup(groupId) {
            return this.$data._data.filter(item =>
                item.group_id === groupId && !item.hasOwnProperty('groupName')
            ).length;
        },
        // Ignored while the archive request is in flight, so a backdrop click
        // cannot dismiss the dialog out from under a running delete.
        closeArchiveModal() {
            if (this.isSaving) {
                return
            }

            this.showArchiveTemplatePanel = false
        },
    },
    computed: {
        showGroupReportForm() {
            return this.templateId !== null && this.useTemplateCards && !! this.schema
        },
        // The report's URL segment; Device Summary's differs from its name.
        groupReportsSlug() {
            return this.reportName === 'weekly-device-summary-report' ? 'weekly-device-summary' : this.reportName
        },
        groupReportsListUrl() {
            return this.company.buildCompanyRoute(`group-reports/${this.groupReportsSlug}?mode=group-reports`)
        },
        newGroupReportUrl() {
            return this.company.buildCompanyRoute(`group-reports/${this.groupReportsSlug}/templates/new`)
        },
        canCreateGroupReport() {
            return this.canEdit && this.permissions.can('create_report_templates')
        },
        groupReportContext() {
            return {can: (permission) => this.permissions.can(permission)}
        },
        // The trigger belongs on the manual-run view only; in the template view
        // settings are reached through a row's Edit action. Hidden when the
        // report has no manual-run settings, so the button never opens an empty
        // drawer.
        showSettingsTrigger() {
            return this.useSettingsDrawer && this.showDisplaySectionManual && this.hasSettings
        },
        // An On-Demand run is a one-off: its settings must not be written back
        // to a saved template, or a user tweaking a single run silently
        // rewrites the scheduled report everyone else receives. The old
        // right-column Save enforced this structurally by rendering only when
        // group reporting was off; the drawer has to carry the same gate.
        // When group reporting is off there is no schedulable template to
        // clobber, so saving the default template stays allowed there.
        // What the error toast says. Server messages are sometimes raw
        // internals (an HTML error page, a PDF renderer's stderr dump); those
        // mean nothing to a user, so anything long or non-prose reads as the
        // generic message, and the detail goes to the console instead.
        runErrorText() {
            const messages = (this.reportRun.state?.errorMessages ?? [])
                .map((message) => presentableRunError(message))
                .filter((message, index, all) => all.indexOf(message) === index)

            return messages.join(' ')
        },
        // Who a run is emailed to: the group on a template's group run,
        // otherwise only the person who ran it.
        runRecipient() {
            return (this.$data._forGroup && this.groupName) ? this.groupName : 'you'
        },
        // The On-Demand card layout from the report designs, for every ported
        // report.
        useCardLayout() {
            return this.useSettingsDrawer
        },
        canSaveFromSettingsDrawer() {
            return this.canEdit && (! this.groupReportingEnabled || this.showDisplaySectionTemplate)
        },
        // True only for a run-and-go report on the manual view: no settings
        // summary to show and no run inputs below it, so the card would
        // otherwise render empty. Scoped to the drawer-based card layout, since
        // the pre-port right-column layout fills the body a different way.
        showNoConfigurationNeeded() {
            return this.useSettingsDrawer
                && this.showDisplaySectionManual
                && ! this.hasSettings
                && ! this.hasRunInputs
        },
        // The at-a-glance chips shown on the Report Settings bar: the same
        // content-adding toggles the template cards summarize, read from the
        // schema + current settings. Empty when the report has no schema or
        // nothing content-adding is on, in which case the bar shows a
        // "default options" line instead.
        settingsSummaryLabels() {
            return enabledFeatureLabels(this.schema, this.$data._settings)
        },
        // The icon shown in the chooser modal, sourced from the active report's
        // schema. GroupReportTemplate does not import a schema itself, so a
        // ported report passes its icon down as a prop. Reports not yet ported
        // pass nothing, so fall back to the shared default glyph rather than
        // render an empty icon tile.
        reportIconSvg() {
            return this.reportIcon ?? DEFAULT_REPORT_ICON
        },
        groupName() {
            return _.has(this.$data._groups, this.$data._groupId)
                ? this.$data._groups[this.$data._groupId]['GroupName']
                : null
        },
        isSavingDefaultTemplate() {
            return this.$data._groupId === 0
        },
        // Only the non-group template carries its own recipient list. A group
        // template's recipients come from the group's membership, and
        // prepareSettings() clears subscribedUsers for it, so offering the
        // picker there would present a choice that is silently discarded.
        showRecipientPicker() {
            return this.isSavingDefaultTemplate && this.reportName !== 'nabp-report'
        },
        groupMemberCount() {
            return this.numberOfUsersOnGroup(this.$data._groupId)
        },
        groupMemberSummary() {
            if (this.groupMemberCount === 0) {
                return 'No users in this group yet'
            }

            return `${this.groupMemberCount} ${this.groupMemberCount === 1 ? 'user' : 'users'} in this group`
        },
        groupSettingsUrl() {
            return this.company.buildCompanyRoute('groups/settings')
        },
        prettyReportName() {
            return REPORT_TEMPLATE_NAMES[this.reportName]
        },
        canManageExcursionReport() {
            return this.canEdit
        },
        // True when the host page passes the rollout option (the decoupled path),
        // regardless of whether it has resolved yet. `undefined` means the page
        // never bound the prop (legacy feature-based path).
        usesDecoupledGroupReporting() {
            return this.useGroupReportingOption !== undefined
        },
        groupReportingEnabled() {
            // Decoupled path: the scheduled/template layer shows only when the
            // option resolves truthy. While loading (null) it stays hidden, so a
            // company never briefly sees group reporting it may not have.
            if (this.usesDecoupledGroupReporting) {
                return this.useGroupReportingOption === true
            }
            // Legacy path: pages not passing the option key off the feature.
            return this.featureLevel.hasFeature(Features.GROUP_REPORTING)
        },
        // Whether the template affordances (mode modal, template sections, and the
        // "Return to Selection" back button) render. A report is templatable only
        // when the company is on group reporting AND the report is not marked
        // on-demand-only. groupReportingEnabled itself stays untouched so a group
        // company's other behavior is unaffected.
        showTemplateAffordances() {
            return this.groupReportingEnabled && ! this.onDemandOnly
        },
        // The header switcher offers On-Demand vs Automatic for a
        // non-group-reportable report that also has an automatic send.
        // (Group-reportable reports use showTemplateAffordances instead.)
        showAutomaticAffordance() {
            return this.onDemandOnly && this.hasAutomaticSend
        },
        showDisplaySelectionModal() {
            return this.$data._displaySection === null
        },
        showDisplaySectionTemplate() {
            return this.$data._displaySection === SHOW_TEMPLATE
        },
        showDisplaySectionManual() {
            return this.$data._displaySection === SHOW_MANUAL
        },
        showDisplaySectionAutomatic() {
            return this.$data._displaySection === SHOW_AUTOMATIC
        },
        // Exposed to the template so the switcher can bind the mode constants
        // without them living in component data.
        SHOW_MANUAL_VALUE() {
            return SHOW_MANUAL
        },
        SHOW_TEMPLATE_VALUE() {
            return SHOW_TEMPLATE
        },
        SHOW_AUTOMATIC_VALUE() {
            return SHOW_AUTOMATIC
        },
        // The copy under the switcher, swapping with the active mode. This is the
        // guidance that used to live in the selection modal; the switcher carries
        // it so the user still learns what each mode does. Automatic is the
        // calibration-style scheduled send; Group Templates is the group case.
        modeDescription() {
            if (this.showDisplaySectionAutomatic) {
                return 'Schedule this report to send automatically to a set list of recipients on a recurring basis.'
            }
            return this.showDisplaySectionTemplate
                ? 'Save settings as a reusable template on a group, schedule it, and send it to everyone in that group.'
                : 'Customize every option and run it now. The report is emailed to you, and only you can see it.'
        },
        // The report header (title/description slot + back button) shows once we
        // are past the mode modal: for a group-reporting company that's after they
        // pick a mode; for a non-group company it's immediately, since they land
        // straight on the on-demand run form (displaySection is set to manual and
        // there is no modal). The old condition (groupReportingEnabled &&
        // displaySection !== null) hid the whole header for non-group companies.
        showReportHeader() {
            return this.$data._displaySection !== null
        },
    },
    watch: {
        // A run that comes back with errors opens the error toast; the next
        // run clears it.
        runErrorText(value) {
            this.showRunError = value !== ''
        },
        // The rollout option loads async, so it may resolve after mount. When it
        // does, apply the on-demand-direct decision (the mounted() call is a
        // no-op if the value was not yet known).
        useGroupReportingOption(value) {
            // A ?mode=group-reports link can open the Group Reports tab before
            // the option loads; a company without group reporting goes back to
            // On Demand.
            if (value === false && this.$data._displaySection === SHOW_TEMPLATE) {
                this.selectDisplaySection(SHOW_MANUAL)
                return
            }
            this.applyGroupReportingDisplaySection()
            // The option can land after the feature data, so settle "standard
            // reporting" again: otherwise the On Demand form keeps showing the
            // template-only Scheduled Delivery card on a group reporting site.
            if (this.$data._displaySection === SHOW_MANUAL) {
                this.$emit('useStandardReporting', ! this.groupReportingEnabled)
            }
        },
        reloadData(val, old) {
            if (val === true && val !== old) {
                this.loadTemplateData()
                this.$emit('update:reloadData', false)
            }
        },
        _forGroup(val, old) {
            if (val !== old) {
                this.$emit('update:forGroup', this.$data._forGroup)
            }
        },
        showIntervalSelectionPanel(val, old) {
            // only reset the current template index if the panel closes (goes from true => false)
            if (old === true && val !== old) {
                this.$data._currentReportTemplateIndex = null
                // A run opened from the Group Reports list sets the id
                // directly; clear it too, or a later On Demand run would
                // still be sent as this group report.
                this.$data._currentReportTemplateId = null
                this.$data._forGroup = false
                this.urlParamsRemove('showIntervalPanel')
                this.urlParamsRemove('forGroup')
            }
        },
        showEditTemplatePanel(val, old) {
            // only reset the current template index if the panel closes (goes from true => false)
            if (old === true && val !== old) {
                this.$data._currentReportTemplateIndex = null
                this.urlParamsRemove('showEditPanel')
            }
        },
        showArchiveTemplatePanel(val, old) {
            // only reset the current template index if the panel closes (goes from true => false)
            if (old === true && val !== old) {
                this.$data._currentReportTemplateIndex = null
            }
        },
        runningReport(val, old) {
            if (val !== old) {
                this.$data._runningReport = val
            }
        },
        _runningReport(val, old) {
            if (val !== old) {
                this.$emit('update:runningReport', val)
            }
        },
        ranReport(val, old) {
            if (val !== old) {
                this.$data._ranReport = val
            }
        },
        _ranReport(val, old) {
            if (val !== old) {
                this.$emit('update:ranReport', val)
            }
        },
        runningErrors: {
            handler(val, old) {
                if (val !== old) {
                    // Reports assign whatever the failed response body was, which
                    // is not always the array this once assumed: a 500 returns an
                    // HTML error page, and a validation failure returns an object.
                    // Normalise so the banner always has something to show.
                    const normalized = this.normalizeErrors(val)

                    // normalizeErrors() always returns a new array, and _runningErrors is
                    // emitted straight back into this prop. Assigning an equal copy would
                    // bounce between the two watchers forever and freeze the tab.
                    if (! _.isEqual(normalized, this.$data._runningErrors)) {
                        this.$data._runningErrors = normalized
                    }
                    this.$data._hasRunningErrors = this.$data._runningErrors.length > 0
                }
            },
            deep: true,
        },
        _runningErrors: {
            handler(val, old) {
                if (val !== old) {
                    this.$emit('update:runningErrors', val)
                }
            },
            deep: true,
        },
        currentReportTemplateId(val, old) {
            if (val !== old) {
                this.$data._currentReportTemplateId = val
            }
        },
        _currentReportTemplateId(val, old) {
            // If the id is null, remove from url params.
            if (_.isNil(val)) {
                this.urlParamsRemove('reportTemplateId')
                if (! _.isNil(old)) {
                    this.$emit('update:currentReportTemplateId', null)
                }
                return
            }

            // If the id has changed (and necessarily not null), update url params and emit event.
            if (val !== old) {
                this.urlParamsStore('reportTemplateId', val)
                this.$emit('update:currentReportTemplateId', val)
                this.$emit('resetSettings')
            }
        },
        _currentReportTemplateIndex(val, old) {
            if (val !== old) {
                // Find the report template id from the index.
                let reportTemplate = this.getReportTemplateByIndex(val)
                this.$data._currentReportTemplateId = _.isNil(reportTemplate)
                    ? null
                    : reportTemplate.id
                console.log(`_currentReportTemplateIndex changed to ${val}, id: ${this.$data._currentReportTemplateId}`)
                this.$emit('update:currentReportTemplateIndex', val)
            }
        },
        interval(val, old) {
            if (val !== old) {
                this.$data._interval = val
            }
        },
        _interval(val, old) {
            if (val !== old) {
                this.$emit('update:interval', val)
                if (this.$data._interval === 'semi-monthly') {

                }
            }
        },
        settings: {
            handler(val, old) {
                if (val !== old) {
                    this.$data._settings = val
                }
            },
            deep: true,
        },
        _settings: {
            handler(val, old) {
                if (val !== old) {
                    this.$emit('update:settings', val)
                }
            },
            deep: true,
        },
        // Search as you type on the card layout. AjaxTable drives its own
        // reload via @loadData, so this is scoped to the card path to avoid
        // firing the same request twice on reports still using the table.
        'ajaxTable.search'(val, old) {
            if (! this.useTemplateCards || val === old) {
                return
            }

            this.debouncedLoadTemplateData()
        },
        _displaySection(val, old) {
            if (val !== old) {
                this.$emit('update:displaySection', this.$data._displaySection)

                // Entering the template view on the card layout: no table is
                // mounted to request the data, so do it here.
                if (this.useTemplateCards && val === SHOW_TEMPLATE) {
                    this.loadGroupReports()
                }
            }
        }
    },
}
</script>

<style scoped>

</style>